import React, { useState, useRef, useEffect } from "react";
import { relatorios } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { 
  Upload, 
  Loader2, 
  FileSpreadsheet, 
  ArrowUpCircle, 
  ArrowDownCircle,
  Save,
  Sparkles,
  Pencil,
  FileText,
  Download
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatCurrency, formatDate, cn } from "../../lib/utils";

export function ContasBancariasPage() {
  const [uploading, setUploading] = useState(false);
  const [extratoResumo, setExtratoResumo] = useState(null);
  const [transacoes, setTransacoes] = useState([]);
  const [editingObs, setEditingObs] = useState(null);
  const [obsValue, setObsValue] = useState("");
  const fileInputRef = useRef(null);
  const { isAdmin } = useAuth();

  // Carregar extrato salvo ao abrir a página
  useEffect(() => {
    const loadSavedExtrato = async () => {
      try {
        const response = await relatorios.getExtratoSalvo();
        if (response.data) {
          const resumo = response.data;
          setExtratoResumo(resumo);
          const mapped = (resumo.transacoes || []).map((item, idx) => ({
            id: `${item.tipo || 'transacao'}-${idx}`,
            tipo: item.tipo || "saida",
            tipo_original: item.tipo_original || "",
            data: item.data || "",
            data_contabil: item.data_contabil || "",
            descricao: item.descricao || "",
            valor: item.valor || 0,
            observacoes: item.observacoes || ""
          }));
          setTransacoes(mapped);
        }
      } catch (error) {
        // Sem extrato salvo, ok
      }
    };
    loadSavedExtrato();
  }, []);

  // Upload de extrato bancário
  const handleExtratoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv"
    ];
    const validExts = ['.xlsx', '.xls', '.csv'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(file.type) && !validExts.includes(ext)) {
      toast.error("Apenas arquivos Excel (.xlsx, .xls) ou CSV são suportados.");
      return;
    }

    setUploading(true);
    setExtratoResumo(null);
    setTransacoes([]);
    
    try {
      const response = await relatorios.analisarExtrato(file);
      const resumo = response.data;
      setExtratoResumo(resumo);
      
      // Mapear todas as transações com campos extras do backend
      const todasTransacoes = (resumo.transacoes || []).map((item, idx) => ({
        id: `${item.tipo || 'transacao'}-${idx}`,
        tipo: item.tipo || "saida",
        tipo_original: item.tipo_original || "",
        data: item.data || "",
        data_contabil: item.data_contabil || "",
        descricao: item.descricao || "",
        valor: item.valor || 0,
        observacoes: ""
      }));
      
      setTransacoes(todasTransacoes);
      toast.success("Extrato importado com sucesso!");
    } catch (error) {
      console.error("Erro ao processar extrato:", error);
      toast.error(error.response?.data?.detail || "Erro ao processar extrato bancário");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Salvar observação de uma transação (persiste no banco)
  const handleSaveObs = async (transacaoId) => {
    const idx = transacoes.findIndex(t => t.id === transacaoId);
    if (idx >= 0) {
      try {
        await relatorios.salvarObservacao(idx, obsValue);
        setTransacoes(prev => prev.map(t => 
          t.id === transacaoId ? { ...t, observacoes: obsValue } : t
        ));
        toast.success("Observação salva!");
      } catch (error) {
        toast.error("Erro ao salvar observação");
      }
    }
    setEditingObs(null);
    setObsValue("");
  };

  // Limpar extrato
  const handleLimparExtrato = () => {
    setExtratoResumo(null);
    setTransacoes([]);
  };

  // Exportar extrato como Excel
  const handleExportarExtrato = async () => {
    try {
      const response = await relatorios.exportarExtrato();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "extrato_bancario.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Extrato exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar extrato");
    }
  };

  return (
    <div className="space-y-6" data-testid="contas-bancarias-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Contas Bancárias</h1>
          <p className="text-muted-foreground">Importação e análise de extratos bancários</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleExtratoUpload}
            accept=".xlsx,.xls,.csv"
            className="hidden"
          />
          <Button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-extrato-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Importar Extrato Excel
              </>
            )}
          </Button>
          {extratoResumo && (
            <>
              <Button variant="outline" onClick={handleExportarExtrato} data-testid="export-extrato-btn">
                <Download className="h-4 w-4 mr-2" />
                Exportar Excel
              </Button>
              <Button variant="outline" onClick={handleLimparExtrato}>
                Limpar
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Resumo do Extrato */}
      {extratoResumo && (
        <div className="space-y-6">
          {/* Header com período */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                Resumo do Extrato
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span className="font-semibold text-blue-900">Período: {extratoResumo.periodo}</span>
                </div>
                <p className="text-sm text-blue-800">{extratoResumo.quantidade_transacoes} transações identificadas</p>
              </div>

              {/* KPIs do Extrato */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-muted-foreground">Saldo Inicial</p>
                  <p className="text-lg font-bold">{formatCurrency(extratoResumo.saldo_inicial)}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-xs text-muted-foreground">Total Entradas</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(extratoResumo.total_entradas)}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-xs text-muted-foreground">Total Saídas</p>
                  <p className="text-lg font-bold text-red-600">{formatCurrency(extratoResumo.total_saidas)}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs text-muted-foreground">Saldo Final</p>
                  <p className={cn(
                    "text-lg font-bold",
                    extratoResumo.saldo_final >= 0 ? "text-blue-600" : "text-red-600"
                  )}>{formatCurrency(extratoResumo.saldo_final)}</p>
                </div>
              </div>

              {/* Análise da IA */}
              {extratoResumo.analise_ia && (
                <div className="mt-4 bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                    <h5 className="font-semibold text-purple-900">Análise Inteligente</h5>
                  </div>
                  <p className="text-sm text-purple-800 whitespace-pre-line">
                    {extratoResumo.analise_ia}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lista de Transações */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Transações do Extrato</CardTitle>
            </CardHeader>
            <CardContent>
              {transacoes.length > 0 ? (
                <div className="border rounded-sm overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 whitespace-nowrap">Data Lanç.</th>
                        <th className="text-left p-3 whitespace-nowrap">Data Contábil</th>
                        <th className="text-left p-3">Tipo</th>
                        <th className="text-left p-3">Descrição</th>
                        <th className="text-right p-3">Valor</th>
                        <th className="text-left p-3 min-w-[200px]">Observações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transacoes.map((transacao) => (
                        <tr key={transacao.id} className="border-t hover:bg-slate-50" data-testid={`transacao-row-${transacao.id}`}>
                          <td className="p-3 whitespace-nowrap">{transacao.data || "-"}</td>
                          <td className="p-3 whitespace-nowrap">{transacao.data_contabil || transacao.data || "-"}</td>
                          <td className="p-3">
                            {transacao.tipo === "entrada" ? (
                              <span className="flex items-center gap-1 text-green-600 whitespace-nowrap">
                                <ArrowUpCircle className="h-4 w-4" />
                                {transacao.tipo_original || "Entrada"}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-red-600 whitespace-nowrap">
                                <ArrowDownCircle className="h-4 w-4" />
                                {transacao.tipo_original || "Saída"}
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            <span className="truncate max-w-[300px] block" title={transacao.descricao}>
                              {transacao.descricao}
                            </span>
                          </td>
                          <td className={cn(
                            "p-3 text-right font-medium",
                            transacao.tipo === "entrada" ? "text-green-600" : "text-red-600"
                          )}>
                            {transacao.tipo === "entrada" ? "+" : "-"}{formatCurrency(transacao.valor)}
                          </td>
                          <td className="p-3">
                            {editingObs === transacao.id ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  className="h-8 text-sm"
                                  value={obsValue}
                                  onChange={(e) => setObsValue(e.target.value)}
                                  placeholder="Digite uma observação..."
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveObs(transacao.id);
                                    if (e.key === 'Escape') setEditingObs(null);
                                  }}
                                  autoFocus
                                />
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  className="h-8 w-8"
                                  onClick={() => handleSaveObs(transacao.id)}
                                >
                                  <Save className="h-4 w-4 text-green-600" />
                                </Button>
                              </div>
                            ) : (
                              <span 
                                className={cn(
                                  "text-muted-foreground cursor-pointer hover:text-foreground",
                                  !transacao.observacoes && "italic"
                                )}
                                onClick={() => {
                                  setEditingObs(transacao.id);
                                  setObsValue(transacao.observacoes || "");
                                }}
                                title="Clique para editar"
                              >
                                {transacao.observacoes || "Adicionar observação..."}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Nenhuma transação identificada no extrato
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Estado vazio */}
      {!extratoResumo && !uploading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileSpreadsheet className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum extrato importado</h3>
            <p className="text-muted-foreground text-center mb-4">
              Faça o upload de um extrato bancário em Excel (.xlsx) para visualizar<br />
              as entradas e saídas da sua conta.
            </p>
            <Button onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-4 w-4 mr-2" />
              Importar Extrato Excel
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
